import React, { useRef } from 'react';
import { Box, Divider } from '@mui/material';
import {
  FormProps,
  FormHeader,
  ClientNameField,
  DocumentUploadArea,
  FactsDescriptionField,
  PieceToRebutField,
  AudioRecordingSection,
  WritingStyleSelector,
  InfoTip,
  PieceTypeSelector,
} from './shared';

// Opções para emenda à inicial
const PECAS_REFERENCIA = [
  'Despacho',
  'Decisão Judicial',
  'Petição Inicial',
  'Outro'
];

const EmendaInicialForm: React.FC<FormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedStyle, setSelectedStyle] = React.useState(values.estilo_escrita || 'padrao');
  const [tipoPeca, setTipoPeca] = React.useState(values.tipo_peca || '');

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  const handleRecordingComplete = (audioBlob: Blob) => {
    const audioFile = new File([audioBlob], `audio_${Date.now()}.webm`, {
      type: 'audio/webm',
    });
    onFileUpload([audioFile]);
  };

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Emenda à Inicial"
        customDescription="Correção ou complementação da petição inicial conforme determinação judicial"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* 1. Tipo da Peça */}
        <PieceTypeSelector
          value={tipoPeca}
          onChange={(value) => {
            setTipoPeca(value);
            handleFieldChange('tipo_peca', value);
          }}
          options={[
            {
              value: 'breve',
              label: 'Breve',
              description: 'Recomenda-se para gerar emendas de juntada de documentos ou esclarecimentos breves, sem exigir teses jurídicas.'
            },
            {
              value: 'longa',
              label: 'Longa',
              description: 'Recomenda-se para gerar peças que exijam maior desenvolvimento argumentativo e teses jurídicas.'
            }
          ]}
        />

        {/* 2. Nome do Cliente */}
        <ClientNameField
          value={values.nome_cliente || ''}
          onChange={(value) => handleFieldChange('nome_cliente', value)}
        />

        {/* 3. Upload de Documentos */}
        <DocumentUploadArea
          uploadedFiles={uploadedFiles}
          onFileUpload={onFileUpload}
          onFileRemove={onFileRemove}
          suggestedDocs="Petição inicial, Despacho de emenda, Documentos faltantes e Procuração (se necessário)."
          fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
        />

        {/* 4. Descrição dos Fatos (Opcional) */}
        <FactsDescriptionField
          value={values.fatos_caso || ''}
          onChange={(value) => handleFieldChange('fatos_caso', value)}
          placeholder="Descreva os esclarecimentos ou correções a serem feitas na petição inicial..."
        />

        {/* 5. Peça de referência */}
        <PieceToRebutField
          value={values.peca_rebater || 'Despacho'}
          onChange={(value) => handleFieldChange('peca_rebater', value)}
          otherValue={values.peca_rebater_outro || ''}
          onOtherChange={(value) => handleFieldChange('peca_rebater_outro', value)}
          options={PECAS_REFERENCIA}
          label="Indique a peça do processo que deseja rebater ou responder:"
        />

        {/* Gravação de Áudio */}
        <AudioRecordingSection
          onRecordingComplete={handleRecordingComplete}
          description="Grave um áudio descrevendo as correções necessárias que a IA irá transcrever automaticamente."
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Estilo de Escrita */}
      <WritingStyleSelector
        selectedStyle={selectedStyle}
        onStyleChange={(styleId) => {
          setSelectedStyle(styleId);
          handleFieldChange('estilo_escrita', styleId);
        }}
      />

      <InfoTip message="Anexe o despacho de emenda para que a IA identifique exatamente quais pontos precisam ser corrigidos ou complementados na petição inicial." />
    </Box>
  );
};

export default EmendaInicialForm;

